import { DayModifiers, RangeModifier } from 'react-day-picker/types/Modifiers';
import { DateUtils, Modifiers } from 'react-day-picker';
import moment from 'moment';
import { ObjectSeasonPrice, ObjectVacationDate } from '../../types/ObjectTypes';

export const getInitialDateRangeHoverState = (): RangeModifier => {
  return {
    from: undefined,
    to: undefined,
  };
};

export const isBetweenTwoRanges = (date: Date, from: Date, to: Date): boolean => {
  return moment(date).isBetween(from, to) || moment(date).isSame(from) || moment(date).isSame(to);
};

export const getMinNightsNotReachedDateRanges = (
  intervals: any[],
  minDays: number,
  selectedRange: RangeModifier,
): RangeModifier[] => {
  if (intervals && minDays) {
    const blockIntervals: RangeModifier[] = [];

    intervals.forEach((interval, idx) => {
      const dateBeforeMinDays = moment(interval.from)
        .subtract(minDays - 1, 'days')
        .toDate();
      if (
        idx === 0 &&
        (!selectedRange.from || !moment(selectedRange.from).isBefore(dateBeforeMinDays))
      ) {
        const beforeFirstInterval = {
          from: dateBeforeMinDays,
          to: interval.from,
        };
        blockIntervals.push(beforeFirstInterval);
      }

      if (
        idx > 0 &&
        (!selectedRange.from ||
          (selectedRange.from &&
            !isBetweenTwoRanges(selectedRange.from, intervals[idx - 1].to, intervals[idx].from)))
      ) {
        const toPrevious = moment(intervals[idx - 1].to);
        const fromCurrent = moment(intervals[idx].from);
        const deltaFromCurrent = fromCurrent.subtract(minDays - 1, 'days').toDate();

        if (toPrevious.isBefore(deltaFromCurrent)) {
          blockIntervals.push({
            from: deltaFromCurrent,
            to: intervals[idx].from,
          });
        } else if (moment(intervals[idx - 1].to).isSame(intervals[idx].from)) {
          blockIntervals.push({
            from: intervals[idx - 1].to,
            to: intervals[idx - 1].to,
          });
        } else {
          blockIntervals.push({
            from: intervals[idx - 1].to,
            to: intervals[idx].from,
          });
        }
      }
    });

    return blockIntervals;
  } else {
    return [];
  }
};

export const getBookedUpDepartureDays = (intervals: RangeModifier[]): Date[] => {
  const departureDays: Date[] = [];
  intervals.forEach(interval => {
    if (interval.to) {
      departureDays.push(interval.to);
    }
  });

  return departureDays;
};

export const isQualifiedToSetDateRange = (minDays: number, from: Date, to: Date): boolean => {
  return moment(to).diff(from, 'days') >= minDays;
};

export const getBookedUpArrivalDays = (intervals: RangeModifier[]): Date[] => {
  const arrivalDays: Date[] = [];
  intervals.forEach(interval => {
    if (interval.from) {
      arrivalDays.push(interval.from);
    }
  });

  return arrivalDays;
};

export const getDisabledDays = (
  bookedUpDates: RangeModifier[],
  minDays: number,
  selectedDateRange: RangeModifier,
): Partial<Modifiers> => {
  const minNightsNotReachedDateRanges = getMinNightsNotReachedDateRanges(
    bookedUpDates,
    minDays,
    selectedDateRange,
  );

  return {
    before: new Date(),
    bookedUp: bookedUpDates,
    minNightsNotReached: minNightsNotReachedDateRanges,
  };
};

export const getModifiers = (
  selectedDateRange: RangeModifier,
  bookedUpDates: { from: Date; to: Date }[],
  minDays: number,
  seasonsPriceData: ObjectSeasonPrice[],
  calcDays: { potentialArrivalDay: boolean; potentialDepartureDay: boolean },
): Partial<Modifiers> => {
  const minNightsNotReachedDateRanges = getMinNightsNotReachedDateRanges(
    bookedUpDates,
    minDays,
    selectedDateRange,
  );
  const modifiedSelectedDateRangeFrom = selectedDateRange.from;
  modifiedSelectedDateRangeFrom?.setHours(0);
  modifiedSelectedDateRangeFrom?.setMinutes(0);
  modifiedSelectedDateRangeFrom?.setSeconds(0);
  modifiedSelectedDateRangeFrom?.setMilliseconds(0);
  const bookedUpArrivalDays = getBookedUpArrivalDays(bookedUpDates);
  const bookedUpDepartureDays = getBookedUpDepartureDays(bookedUpDates);
  const potentialArrivalDays = calcDays.potentialArrivalDay
    ? getAvailableDays({
        seasonsPriceData,
        key: 'arrivalDays',
        blockedDateRanges: bookedUpDates,
        afterThreshold: new Date(),
      })
    : [];

  const potentialDepartureDay = calcDays.potentialDepartureDay
    ? getAvailableDays({
        seasonsPriceData,
        key: 'departureDays',
        afterThreshold: modifiedSelectedDateRangeFrom,
        blockedDateRanges: bookedUpDates,
      })
    : [];

  return {
    start: selectedDateRange.from ? selectedDateRange.from : new Date(),
    end: selectedDateRange.to ? selectedDateRange.to : new Date(),
    bookedUp: bookedUpDates,
    minNightsNotReached: minNightsNotReachedDateRanges,
    bookedUpArrivalDay: bookedUpArrivalDays,
    bookedUpDepartureDay: bookedUpDepartureDays,
    potentialArrivalDay: potentialArrivalDays,
    potentialDepartureDay: potentialDepartureDay,
  };
};

export const isSelectingFrom = (range: RangeModifier): boolean => {
  return !range.from && !range.to;
};

export const isSelectingTo = (range: RangeModifier): boolean => {
  return !!range.from && !range.to;
};

export const isSelectingToAgain = (range: RangeModifier): boolean => {
  return !!range.from && !!range.to;
};

export const getDisabledDaysBeforeToday = (
  disabledDays: Partial<Modifiers> = {},
): Partial<Modifiers> => {
  return { ...disabledDays, before: new Date() };
};

export const canBeSelected = (modifiers: DayModifiers): boolean => {
  return (
    !modifiers.disabled &&
    !modifiers.minNightsNotReached &&
    (!!modifiers.selected ||
      !!modifiers.potentialArrivalDay ||
      !!modifiers.potentialDepartureDay ||
      (!!modifiers.bookedUp &&
        !!modifiers.bookedUpDepartureDay &&
        !!modifiers.potentialArrivalDay) ||
      (!!modifiers.bookedUp && !!modifiers.bookedUpArrivalDay && !!modifiers.potentialDepartureDay))
  );
};

export const hasSelectedOverBookedUp = (
  selectedRange: RangeModifier,
  modifiers: { [key: string]: boolean },
  disabledDays: Partial<Modifiers> | undefined,
): boolean => {
  if (disabledDays?.bookedUp) {
    const { bookedUp } = disabledDays;

    return (bookedUp as RangeModifier[]).some((bookedUpDate, idx) => {
      if (selectedRange.from && selectedRange.to && bookedUpDate.from && bookedUpDate.to) {
        return (
          (DateUtils.isSameDay(selectedRange.from, bookedUpDate.from) ||
            DateUtils.isDayBefore(selectedRange.from, bookedUpDate.from)) &&
          (DateUtils.isSameDay(selectedRange.to, bookedUpDate.to) ||
            DateUtils.isDayAfter(selectedRange.to, bookedUpDate.to))
        );
      } else {
        return false;
      }
    });
  }
  return false;
};

export const isQualifiedToUpdateFromAndToDate = (params: {
  objectsParamsFrom?: string;
  objectsParamsTo?: string;
  currentFrom: string;
  currentTo: string;
}): boolean => {
  const { objectsParamsFrom, objectsParamsTo, currentFrom, currentTo } = params;
  return objectsParamsFrom !== currentFrom || objectsParamsTo !== currentTo;
};

export const getWeekDaysAsNumber = (weekDays: string[]): number[] => {
  return weekDays.map(weekDay => {
    switch (weekDay) {
      case 'Mon':
        return 1;
      case 'Tue':
        return 2;
      case 'Wed':
        return 3;
      case 'Thu':
        return 4;
      case 'Fri':
        return 5;
      case 'Sat':
        return 6;
      case 'Sun':
        return 0;
      default:
        return -1;
    }
  });
};

export const getAvailableDaysWithoutBlockedDays = (
  availableDays: Date[],
  blockedDateRanges: { from: Date; to: Date }[],
): Date[] => {
  return availableDays.filter(availableDay => {
    const isBlocked = blockedDateRanges.find(blockedDateRange => {
      return moment(availableDay).isBetween(blockedDateRange.from, blockedDateRange.to, 'day');
    });

    return !isBlocked;
  });
};

export const arrivalHasOverlapWithBlockedDateRange = (params: {
  currentDate: Date;
  blockedDateRanges: { from: Date; to: Date }[];
  minDays: number;
}): boolean => {
  const { currentDate, blockedDateRanges, minDays } = params;

  return !!blockedDateRanges.find(blockedDateRange => {
    const res1 = moment(currentDate).isBetween(blockedDateRange.from, blockedDateRange.to, 'days');
    const res2 =
      moment(currentDate)
        .add(minDays, 'days')
        .isBetween(blockedDateRange.from, blockedDateRange.to, 'days') ||
      moment(currentDate).isSame(blockedDateRange.from, 'days');
    return res1 || res2;
  });
};

/**
 * Get all available days where the user can click on datepicker day
 * @param params
 */
export const getAvailableDays = (params: {
  seasonsPriceData: ObjectSeasonPrice[];
  key: 'arrivalDays' | 'departureDays';
  afterThreshold?: Date | undefined | null;
  blockedDateRanges: { from: Date; to: Date }[];
}): Date[] => {
  const { seasonsPriceData, key, afterThreshold, blockedDateRanges } = params;
  const availableDays: Date[] = [];
  let cancelLoop = false;
  // find all days which has a match with ['Mon', 'Thu', 'Wed'] and so on
  seasonsPriceData.forEach(priceData => {
    if (!cancelLoop) {
      const momentFrom = moment(priceData.from);
      const momentTo = moment(priceData.to);
      const diff = momentTo.diff(momentFrom, 'days');

      for (let i = 0; i <= diff; i++) {
        const currentDate = moment(priceData.from).add(i, 'days');
        const days = key === 'arrivalDays' ? priceData.arrivalDays : priceData.departureDays;
        const weekDaysAsNumber = getWeekDaysAsNumber(days);

        const hasReachedMinOvernights =
          (key === 'departureDays' &&
            currentDate.diff(afterThreshold, 'days') >= priceData.minimumLength) ||
          key === 'arrivalDays';

        const isBlocked =
          key === 'departureDays' &&
          currentDate.isAfter(afterThreshold) &&
          blockedDateRanges.find(blockedDateRange => {
            return moment(currentDate).isBetween(blockedDateRange.from, blockedDateRange.to, 'day');
          });

        const arrivalOverlapsBlockedDateRange =
          key === 'arrivalDays' &&
          afterThreshold &&
          arrivalHasOverlapWithBlockedDateRange({
            currentDate: currentDate.toDate(),
            blockedDateRanges,
            minDays: priceData.minimumLength,
          });
        if (
          (!arrivalOverlapsBlockedDateRange &&
            hasReachedMinOvernights &&
            weekDaysAsNumber.indexOf(currentDate.days()) !== -1 &&
            !isBlocked &&
            (!afterThreshold || currentDate.isSameOrAfter(afterThreshold, 'day'))) ||
          (days.length === 0 && hasReachedMinOvernights)
        ) {
          availableDays.push(currentDate.toDate());
        }

        // cancel on first reached block range
        if (isBlocked) {
          cancelLoop = true;
          break;
        }
      }
    }
  });

  return getAvailableDaysWithoutBlockedDays(availableDays, blockedDateRanges);
};

export const isAvailableDay = (
  seasonsPriceData: ObjectSeasonPrice[],
  date: Date,
  key: 'arrivalDays' | 'departureDays',
): boolean => {
  let isAvailableDay = false;

  seasonsPriceData.forEach(priceData => {
    const momentFrom = moment(priceData.from);
    const momentTo = moment(priceData.to);
    const diff = momentTo.diff(momentFrom, 'days');

    for (let i = 0; i <= diff; i++) {
      const currentDate = moment(priceData.from).add(i, 'days');
      const days = key === 'arrivalDays' ? priceData.arrivalDays : priceData.departureDays;
      const weekDaysAsNumber = getWeekDaysAsNumber(days);

      if (weekDaysAsNumber.indexOf(currentDate.days()) !== -1) {
        isAvailableDay = true;
      }
    }
  });

  return isAvailableDay;
};

export const getBookedUpDateFromToday = (
  vacationsData: ObjectVacationDate[],
  afterThreshold: Date,
) => {
  return vacationsData
    .filter(vacation => {
      return moment(vacation.fromDate).isAfter(afterThreshold);
    })
    .map(vacation => {
      return { from: new Date(vacation.fromDate), to: new Date(vacation.toDate) };
    });
};
